import * as React from 'react';
import { useAtom } from 'jotai';
import { atom } from 'jotai';
import { useAtomValue } from 'jotai/utils';
import { useAuthUser } from 'react-auth-kit';

import AdminSettingsSection from 'src/components/AdminSettingsSection';
import Checkbox from 'src/components/Checkbox';
import { Input } from 'src/components';
import { useSettingsQuery } from 'src/api/queries/userQueries';
import { useSubscriptionQuery } from 'src/api/queries/subscriptionQueries';
import { contentVersioningSettingsAtom, contentVersioningState } from 'src/shared/settings/contentVersioning';
import { ACTION_TYPES } from 'src/shared/settings/base';
import { ReleaseRole } from 'src/types/TAssets';

import ExemptUsersPicker from './ExemptUsersPicker';
import styles from './styles.module.scss';

const MAX_PREVIOUS_VERSIONS = 10;

const DEFAULT_RELEASE_ROLES: ReleaseRole[] = ['ADMIN', 'MANAGER'];
const RELEASE_ROLE_OPTIONS: { role: ReleaseRole; label: string }[] = [
  { role: 'ADMIN', label: 'Admin' },
  { role: 'MANAGER', label: 'Manager' },
  { role: 'CREATOR', label: 'Creator' },
  { role: 'USER', label: 'User' },
];

type ContentVersioningSettings = {
  keep_history: boolean;
  require_approval: boolean;
  exempt_user_ids: number[];
  gate_ai_edits: boolean;
  max_previous_versions: number;
  release_roles: ReleaseRole[];
};

const stateAtom = atom((get) => contentVersioningState(get) as Partial<ContentVersioningSettings>);

/**
 * Account-wide content versioning: keep previous versions when an asset's
 * content is replaced, and optionally hold replacements until someone makes
 * them live. Saved through the standard settings mutation (key
 * `content_versioning`) — pair with `SaveChangesBar` on the page.
 */
export const ContentVersioningSetting: React.FC = () => {
  useSettingsQuery();
  const [, update] = useAtom(contentVersioningSettingsAtom);
  const settings = useAtomValue(stateAtom);
  const auth = useAuthUser();
  const { data: subscription } = useSubscriptionQuery({ retry: false });
  const isOwner = !!subscription?.owner_user_id && subscription.owner_user_id === auth()?.userId;

  const set = (payload: Partial<ContentVersioningSettings>) =>
    update({ type: ACTION_TYPES.UPDATE_STATE, payload });

  const keepHistory = !!settings.keep_history;
  const requireApproval = !!settings.require_approval;
  const exempt = settings.exempt_user_ids ?? [];
  const maxPrevious = settings.max_previous_versions ?? 2;
  const releaseRoles = settings.release_roles?.length ? settings.release_roles : DEFAULT_RELEASE_ROLES;
  const toggleRole = (role: ReleaseRole, on: boolean) => {
    const next = RELEASE_ROLE_OPTIONS.map((o) => o.role).filter((r) =>
      r === role ? on : releaseRoles.includes(r),
    );
    if (next.length) set({ release_roles: next });
  };


  return (
    <>
      <AdminSettingsSection title="Version history" className={styles.section}>
        <Checkbox
          label="Keep the previous version when an asset's content is replaced"
          checked={keepHistory || requireApproval}
          disabled={requireApproval}
          onChange={(e) => set({ keep_history: e.target.checked })}
          datatest="versioning-keep-history"
        />
        <p className={styles.description}>
          Previous versions are listed on the asset's Versions tab, where they can be downloaded or
          restored. They count towards your storage. When this is off, replacing an asset overwrites it.
        </p>

        <label className={styles.numberField}>
          <span>Previous versions kept per asset</span>
          <Input
            type="number"
            variant="secondary"
            min={0}
            max={MAX_PREVIOUS_VERSIONS}
            value={String(maxPrevious)}
            disabled={!isOwner}
            onChange={(value: string) => {
              const n = Math.max(0, Math.min(MAX_PREVIOUS_VERSIONS, parseInt(value, 10) || 0));
              set({ max_previous_versions: n });
            }}
            datatest="versioning-max-previous"
          />
        </label>
        <p className={styles.description}>
          From 0 to {MAX_PREVIOUS_VERSIONS}; the oldest is deleted when a new version goes live.
          {!isOwner && ' Only the account owner can change this.'}
        </p>
      </AdminSettingsSection>

      <AdminSettingsSection title="Release approval" className={styles.section}>
        <Checkbox
          label="Hold replacements until they are made live"
          checked={requireApproval}
          onChange={(e) => set({ require_approval: e.target.checked })}
          datatest="versioning-require-approval"
        />
        <p className={styles.description}>
          A replaced file becomes a version waiting for release; the current file keeps serving
          everywhere until someone in a release role makes the new version live from its Versions tab.
        </p>

        {requireApproval && (
          <div className={styles.roles} data-test="versioning-release-roles">
            <span className={styles.rolesTitle}>Who can release</span>
            {RELEASE_ROLE_OPTIONS.map(({ role, label }) => (
              <Checkbox
                key={role}
                label={label}
                checked={releaseRoles.includes(role)}
                disabled={releaseRoles.length === 1 && releaseRoles.includes(role)}
                onChange={(e) => toggleRole(role, e.target.checked)}
                datatest={`versioning-release-role-${role.toLowerCase()}`}
              />
            ))}
            <p className={styles.description}>
              People in these roles can make versions live, reject them and restore previous ones, on
              assets they can edit. They get one notice that counts the assets awaiting release and a
              daily email summary. Anyone who can add assets can submit a version.
            </p>
          </div>
        )}

        {requireApproval && (
          <ExemptUsersPicker selectedIds={exempt} onChange={(ids) => set({ exempt_user_ids: ids })} />
        )}

        <span className={styles.rolesTitle}>AI edits</span>
        <Checkbox
          label="Apply to AI edits saved over the original"
          checked={!!settings.gate_ai_edits}
          disabled={!keepHistory && !requireApproval}
          onChange={(e) => set({ gate_ai_edits: e.target.checked })}
          datatest="versioning-gate-ai-edits"
        />
        <p className={styles.description}>
          When on, an AI edit saved over an asset's original is handled like any other replacement.
        </p>
      </AdminSettingsSection>
    </>
  );
};

export default ContentVersioningSetting;
